/* eslint no-shadow: ['error', { 'allow': ['state', 'getters'] }] */
import axios from 'axios';
import { fcoUrl } from 'fcoModules/utilities';
import { CacheKey, getCacheRefreshKey, requestCacheTime, getStorageItem, StorageKey, setStorageItem } from '../store-utils';
import * as quoteService from '../../services/quoteService';
import { createAxiosResponse, createRequestStatus, trackRequestStatus, updateRequestStatus } from '../request-status';

const getDefaultQuoteData = () => ({
    subTotalCost: 0,
    subTotalPrice: 0,
    totalItems: 0,
    worksheetHeaderId: null,
});

export const state = {
    expandMiniQuote: false,
    quoteProducts: [],
    quoteDetails: {
        ...getDefaultQuoteData(),
        worksheetHeaderId: getStorageItem(StorageKey.CURRENT_WORKSHEET_ID) || null,
    },
    requests: {
        getQuoteData: createRequestStatus({
            cacheKey: CacheKey.WORKSHEET,
            pollKey: 'worksheet',
            pollValue: ({ state }) => state.miniQuote?.quoteDetails?.worksheetHeaderId,
        }),
    },
};

export const mutations = {
    updateRequestStatus,
    setExpandMiniQuote(state, val) {
        state.expandMiniQuote = val;
    },
    setQuoteData(state, { quoteDetails = [], subtotalCost, subtotalPrice, totalItems, worksheetHeaderId }) {
        state.quoteProducts = quoteDetails;
        state.quoteDetails = {
            subtotalCost,
            subtotalPrice,
            totalItems,
            worksheetHeaderId,
        };
        requestCacheTime[getCacheRefreshKey(CacheKey.WORKSHEET)] = new Date();
        if (worksheetHeaderId) {
            setStorageItem(StorageKey.CURRENT_WORKSHEET_ID, worksheetHeaderId);
        } else {
            localStorage.removeItem(StorageKey.CURRENT_WORKSHEET_ID);
        }
    },
    resetQuoteData(state) {
        state.quoteProducts = [];
        state.quoteDetails = getDefaultQuoteData();
        localStorage.removeItem(StorageKey.CURRENT_WORKSHEET_ID);
    },
};

export const actions = {
    openMiniQuote({ commit }) {
        commit('setExpandMiniQuote', true);
    },
    closeMiniQuote({ commit }) {
        commit('setExpandMiniQuote', false);
    },
    async getQuoteData({ state, commit, dispatch, rootGetters }) {
        const response = await trackRequestStatus(
            commit,
            'getQuoteData',
            (async () => {
                await dispatch('requestIfIdle', ['getFeatures', 'getFeaturesForStore'], { root: true });
                if (rootGetters.isFeatureDisabled('entsearch')) {
                    return quoteService.getMiniQuoteLegacy();
                }
                const { worksheetHeaderId } = state.quoteDetails;
                if (!worksheetHeaderId) return createAxiosResponse(getDefaultQuoteData());
                return quoteService.getMiniQuote(state.quoteDetails.worksheetHeaderId);
            })()
        );
        commit('setQuoteData', response?.data ?? getDefaultQuoteData());
    },
    async clearQuote({ commit, dispatch }) {
        commit('resetQuoteData');
        dispatch('usageTracking/clearLookups', null, { root: true });
    },
    async addProduct({ dispatch }, { product, quantity }) {
        const { data } = await quoteService.addProduct(product, quantity);
        // The addproduct response does not include updated quote info (like totals or discounts), so we need to get the latest
        await dispatch('getQuoteData');
        return data;
    },
    async addProducts({ dispatch, getters, rootGetters }, { products, worksheetVehicleId = 0 }) {
        if (!getters.currentQuoteId) {
            await dispatch('newQuote');
        }
        const worksheetId = getters.currentQuoteId;
        await dispatch('requestIfIdle', ['getFeatures', 'getFeaturesForStore'], { root: true });
        const { data } = await quoteService.addProducts(worksheetId, products, worksheetVehicleId, !rootGetters.isFeatureDisabled('entsearch'));
        // The addproduct response does not include updated quote info (like totals or discounts), so we need to get the latest
        await dispatch('getQuoteData');
        return data;
    },
    async addKitProducts({ dispatch }, { products, isPrebuilt }) {
        const { data } = await quoteService.addKitProducts(products, isPrebuilt);

        // The addkit response does not include updated quote info (like totals or discounts), so we need to get the latest
        await dispatch('getQuoteData');
        return data;
    },
    async addKitProductsEnterprise({ dispatch }, { products, worksheetVehicleId, isPrebuilt = false }) {
        const { worksheetHeaderId: worksheetId } = state.quoteDetails;
        const { data } = await quoteService.addKitProductsEnterprise({ products, worksheetId, worksheetVehicleId, isPrebuilt });

        // The addkit response does not include updated quote info (like totals or discounts), so we need to get the latest
        await dispatch('getQuoteData');
        return data;
    },
    async setCurrentQuote({ commit, dispatch }, worksheetHeaderId) {
        commit('setQuoteData', { ...getDefaultQuoteData(), worksheetHeaderId });
        await dispatch('getQuoteData');
    },
    async newQuote({ dispatch, getters, rootState, commit }) {
        dispatch('clearQuote');
        await dispatch('requestIfIdle', ['getFeatures', 'getFeaturesForStore'], { root: true });
        if (getters.isEntSearchDisabled) {
            await axios.post(fcoUrl('/vehicle/select/default'), { id: 0 });
        } else {
            await dispatch('requestIfIdle', ['getCurrentShop'], { root: true });
            const shopId = rootState.currentShop?.id;
            // Without a shopId, the newQuote call will fail. Throw an error so we can catch it in the UI and show a message.
            if (!shopId) throw new Error('No shop selected');
            const { data: worksheetHeaderId } = await quoteService.newQuote(rootState.currentShop?.id);
            commit('setQuoteData', { ...getDefaultQuoteData(), worksheetHeaderId });
        }
        await dispatch('getQuoteData');
    },
};

const getters = {
    isEntSearchDisabled: (state, getters, rootState, rootGetters) => rootGetters.isFeatureDisabled('entsearch'),
    currentQuoteId: (state) => state.quoteDetails.worksheetHeaderId,
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
